import React, { useEffect, useRef, useState } from 'react';
import DashboardLayout from '@layouts/dashboard/dashboard-layout';
import ExpenseTable from './table';
import { useNavigate } from 'react-router-dom';
import { QueryRequestHelper } from '@common/query-request-helper';
import {
  deleteSpecificExpenseEntry,
  getAllExpenseEntries,
} from '@services/expense-entry.service';
import { getValue } from '@utils/lodash';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import DeleteModal from '@common/popup/DeleteModal';
import PaginationPage from '@components/pagination/pagination';
import NavigationBar from '@components/NotificationBar';
import SearchHeader from '@components/common/SearchHeader';
import Loader from '@components/common/Loader';
import Notfound from '@components/Not-Found';
import ReportsTable from './table';
import {
  createExpenseReport,
  getAllExpenseFormReport,
  getAllExpenseReportFormEdit,
  getAllExpenseReports,
} from '@services/expense-reports.service';
import { convertCurrentDateWithTime, convertDate } from '@common/date-helpers';
import SimpleReactValidator from 'simple-react-validator';
import ReportsPopup from '../../../components/Reports/components/report-add-popup';
import { FormRequestHelper } from '@components/common/FormBuilder/request-helper';
import {
  formatText,
  removeNullOrUndefinedProperties,
} from '@common/text-helpers';
import NoAccessPage from '@components/common/NoAccess';
import ReportNotfound from '@components/Not-Found/report-not-found';
import SkeletonTable from '@components/common/Skeleton/skeleton-table';
import { useNavigationData } from '@components/common/LeftNavbar/NavigationContext';
import { getMyProfile } from '@services/users.service';
import Select from 'react-select';
import { usersSearchableDropdown } from '@components/common/CustomSearchableDropdown/SearchAPIs';
import GuidelinesPopup from './components/GuidelinesPopup';

let hasOpenedGuidelinesOnce = false;

function ClientReports(props: any) {
  let navigate = useNavigate();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const urlParams = Object.fromEntries(urlSearchParams.entries());
  let mainQueryRequest = QueryRequestHelper(urlParams);
  const { info } = useNavigationData();

  const simpleValidator = useRef(new SimpleReactValidator());
  const [, forceUpdate] = useState(0);
  /* -------------------------------------------------------------------------- */
  /*                               UseState Section                             */
  /* -------------------------------------------------------------------------- */

  const [isLoading, setIsLoading] = React.useState(true);
  const [list, setList] = React.useState<any[]>([]);
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const handleOpenPopup = () => {
    setShowUpdatePopup(!showUpdatePopup);
  };
  const [showGuidelines, setShowGuidelines] = useState(false);
  const handleGuidelinesModal = () => {
    setShowGuidelines(!showGuidelines);
  };

  // Created For dropdown states
  const [userProfile, setUserProfile] = useState<any>({});
  const [createdFor, setCreatedFor] = useState<any>(null);
  const [createdForOptions, setCreatedForOptions] = useState([
    { value: 'self', label: 'Self' },
  ]);

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: '32px',
      height: '32px',
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      height: '32px',
      padding: '0 6px',
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0px',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: '32px',
    }),
  };

  const getUserProfileData = async () => {
    try {
      let resp = await getMyProfile('');
      if (resp) {
        const profileData = getValue(resp, `data`, {});
        setUserProfile(profileData);
        setCreatedFor({ value: 'self', label: 'Self' });
        await getDelegatedUsersWithUserId(getValue(profileData, `id`, ''));
        return profileData;
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
    return {};
  };

  const applyDefaultProjectToForm = (formLayout: any[] = [], profile: any = {}) =>
    formLayout.map((item: any) => {
      if (
        getValue(item, `field_name`, '') === 'project' &&
        !getValue(item, `field_value`, '')
      ) {
        return {
          ...item,
          field_value: getValue(profile, `project.id`, ''),
        };
      }
      if (
        getValue(item, `field_name`, '') === 'employee_cost_center' &&
        !getValue(item, `field_value`, '')
      ) {
        return {
          ...item,
          field_value: getValue(profile, `employee_cost_center`, ''),
        };
      }
      return item;
    });

  const getDelegatedUsersWithUserId = async (userId: string | number) => {
    if (!userId) return;

    try {
      const userIdNumber =
        typeof userId === 'string' ? parseInt(userId, 10) : userId;

      const delegatedUsers = await usersSearchableDropdown(
        '',
        '',
        userIdNumber,
      );

      if (delegatedUsers && delegatedUsers.length > 0) {
        const delegatedOptions = delegatedUsers.map((user: any) => ({
          value: String(getValue(user, `id`, '')),
          label: getValue(user, `workflow_name`, ''),
        }));

        setCreatedForOptions([
          { value: 'self', label: 'Self' },
          ...delegatedOptions,
        ]);
      } else {
        setCreatedForOptions([{ value: 'self', label: 'Self' }]);
      }
    } catch (error) {
      console.error('Error fetching delegated users:', error);
      setCreatedForOptions([{ value: 'self', label: 'Self' }]);
    }
  };

  const handleCreatedForChange = (selectedOption: any) => {
    setCreatedFor(selectedOption);
  };

  /* -------------------------------------------------------------------------- */
  /*                               UseEffect Section                            */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const init = async () => {
      const profileData = await getUserProfileData();
      if (Object.keys(urlParams).length === 0 && props.permissionAPITriggered) {
        getData();
        getFormData(profileData);
      }
    };
    init();
  }, [props.permissionAPITriggered]);

  useEffect(() => {
    const init = async () => {
      if (Object.keys(urlParams).length > 0 && props.permissionAPITriggered) {
        const profileData = await getUserProfileData();
        getData();
        if (urlParams.page_no) {
          setPage_no(parseInt(urlParams.page_no));
        }
        if (urlParams.search_text) {
          setSearchInput(urlParams.search_text);
          setShowSearchTextbox(true);
        }
        if (urlParams.limit) {
          setLimit(parseInt(urlParams.limit));
        }
        getFormData(profileData);
      }
    };
    init();
  }, [window.location.href, props.permissionAPITriggered]);

  useEffect(() => {
    const userId = getValue(userProfile, 'id', '');
    if (userId && !hasOpenedGuidelinesOnce) {
      const guidelines = getValue(userProfile, 'policy.business_expense_guidelines', '') || '';
      const hasGuidelines = guidelines.replace(/<[^>]*>/g, '').trim().length > 0;

      if (hasGuidelines) {
        setShowGuidelines(true);
        hasOpenedGuidelinesOnce = true;
      }
    }
  }, [userProfile]);

  /* -------------------------------------------------------------------------- */
  /*                               API Section                                  */
  /* -------------------------------------------------------------------------- */

  const getData = async () => {
    let payload: any = {
      page_no: urlParams.page_no ? parseInt(urlParams.page_no) : 1,
      page_size: urlParams.limit ? parseInt(urlParams.limit) : limit,
      search_text: urlParams.search_text,
      // status: urlParams.status,
      filter_type: 'list',
      sort_by: urlParams.sort_by,
      approval_status: getValue(urlParams, `approval_status`, ''),
    };

    // Add delegate filter if not "self"
    if (createdFor && getValue(createdFor, `value`, '') !== 'self') {
      payload.submitted_for = Number(getValue(createdFor, `value`, ''));
    }
    let queryRequest = QueryRequestHelper(payload);
    try {
      setIsLoading(true);
      let resp = await getAllExpenseReports(queryRequest);
      if (resp) {
        setList(getValue(resp, `data.expense_reports`, []));
        setTotalCount(getValue(resp, `data.pagination.total`, 0));
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  // Re-fetch data when createdFor changes
  useEffect(() => {
    if (createdFor && props.permissionAPITriggered) {
      getData();
    }
  }, [createdFor]);

  /* -------------------------------------------------------------------------- */
  /*                               Search section                               */
  /* -------------------------------------------------------------------------- */
  const [showSearchTextbox, setShowSearchTextbox] = useState(false);
  const [search_text, setSearchInput] = useState<any>('');

  const toggleSearch = () => {
    setShowSearchTextbox((prevState) => !prevState);
  };
  const toggleSearchClose = () => {
    setSearchInput('');
    toggleSearch();
    if (urlParams.search_text) {
      delete urlParams.search_text;
      let payload: any = { ...urlParams };
      let queryRequest = QueryRequestHelper(payload);

      navigate(`${window.location.pathname}?${queryRequest}`);
      getData();
    }
  };
  const handleChangeSearch = async (e: any) => {
    setSearchInput(e.target.value);

    let payload: any = {
      ...urlParams,
      page_no: 1,
      search_text: e.target.value,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    if (!e.target.value) {
      getData();
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               Onchange section                             */
  /* -------------------------------------------------------------------------- */

  const [selectedIDs, setSelectedIDs] = useState<any>([]);
  const handleCheckUsers = (id: string) => {
    let userIDs =
      getValue(selectedIDs, `length`, 0) > 0
        ? selectedIDs.map((item: string) => item)
        : [];
    if (userIDs.includes(id)) {
      let filterIDs = selectedIDs.filter((item: string) => item !== id);
      setSelectedIDs(filterIDs);
    } else {
      selectedIDs.push(id);
      setSelectedIDs([...selectedIDs]);
    }
  };
  const handleCheckAll = (status: string) => {
    if (status === 'All') {
      setSelectedIDs([]);
    } else {
      let userIDs =
        getValue(list, `length`, 0) > 0
          ? list.map((item: object) => getValue(item, `id`, ''))
          : [];
      setSelectedIDs(userIDs);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                 Delete section                             */
  /* -------------------------------------------------------------------------- */

  const [deleteValue, setDeleteValue] = useState('');
  const [deleteId, setDeleteId] = useState('');
  const handleDeleteFun = (id: string, value: string) => {
    setDeleteValue(value);
    setDeleteId(id);
    handleDelete();
  };
  const [isDelete, setIsDelete] = useState(false);
  const handleDelete = () => {
    setIsDelete(!isDelete);
  };
  const handleDeleteFunction = async () => {
    try {
      let resp = await deleteSpecificExpenseEntry(deleteId);
      if (resp) {
        toast.success(getValue(resp, `message`, ''));
        handleDelete();
        getData();
      }
    } catch (error) {}
  };

  /* -------------------------------------------------------------------------- */
  /*                               Pagination section                           */
  /* -------------------------------------------------------------------------- */

  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(10);
  const [page_no, setPage_no] = useState(1);

  const handleChangePagination = (page_no: string) => {
    let payload = {
      ...urlParams,
      page_no: page_no,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };
  const handleChangeLimit = (limit: string) => {
    let payload: any = {
      ...urlParams,
      limit: limit,
      page_no: '1',
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };
  const handleChangeActiveStatus = async (value: string) => {
    // try {
    //   let resp = await adminBulkUpdateStatus({
    //     ids: selectedIDs,
    //     is_active: value === "active" ? true : false,
    //   });
    //   if (resp) {
    //     toast.success(getValue(resp, `message`, ""));
    //     setSelectedIDs([]);
    //     getData();
    //   } else {
    //     getData();
    //   }
    // } catch (error) {}
  };
  const [check, setCheck] = useState(false);
  const handleRemoveNavItems = () => {
    setCheck(!check);
    setSelectedIDs([]);
  };
  const handleNavigateUserEntity = () => {
    if (getValue(selectedIDs, `[${0}]`, '')) {
      navigate(
        `/admin/user/entity-mapping/${getValue(selectedIDs, `[${0}]`, '')}`,
      );
    } else {
      toast.error('Please select User');
    }
  };
  const handleNavigate = (id: string) => {
    const createdForId = getValue(createdFor, `value`, '');
    const createdForName = getValue(createdFor, `label`, '');
    const createdForSuffix =
      createdForId && createdForId !== 'self'
        ? `&created_for=${createdForId}&created_for_name=${encodeURIComponent(
            createdForName || String(createdForId),
          )}`
        : '';

    navigate(
      `/client/reports/detail/${id}?${mainQueryRequest}${createdForSuffix}`,
    );
  };
  /* -------------------------------------------------------------------------- */
  /*                                 Create section                             */
  /* -------------------------------------------------------------------------- */
  const getFormData = async (profileOverride: any = null) => {
    let payload: any = {};
    let queryRequest = QueryRequestHelper(payload);
    try {
      setIsLoading(true);
      let resp = await getAllExpenseFormReport(queryRequest);
      if (resp) {
        setFormInfo(getValue(resp, `data`, {}));
        let form = getValue(resp, `data.form_layout`, []).map((item: any) => {
          if (getValue(item, `field_name`, '') === 'duration_from') {
            const now = new Date();
            const firstDayOfMonth = new Date(
              now.getFullYear(),
              now.getMonth(),
              1,
            );
            item['field_value'] = convertDate(firstDayOfMonth);
          } else if (getValue(item, `field_name`, '') === 'duration_to') {
            item['field_value'] = convertDate(new Date());
          }
          return item;
        });
        form = applyDefaultProjectToForm(form, profileOverride || userProfile);
        setForm(form);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const [form, setForm] = React.useState<any[]>([]);
  const [formInfo, setFormInfo] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (
      (getValue(userProfile, `project.id`, '') ||
        getValue(userProfile, `employee_cost_center`, '')) &&
      getValue(form, `length`, 0) > 0
    ) {
      setForm((prevForm: any) => applyDefaultProjectToForm(prevForm, userProfile));
    }
  }, [userProfile]);

  const handleSubmit = async () => {
    const formValid = simpleValidator.current.allValid();
    if (!formValid) {
      simpleValidator.current.showMessages();
      forceUpdate(1);
    } else {
      try {
        setSubmitLoading(true);
        let fields = JSON.parse(JSON.stringify(form));
        let payload = FormRequestHelper(fields);
        let requestPayload: any = payload;
        if (createdFor && getValue(createdFor, `value`, '') !== 'self') {
          requestPayload.submitted_for = Number(
            getValue(createdFor, `value`, ''),
          );
        }
        let resp = await createExpenseReport(
          removeNullOrUndefinedProperties(requestPayload),
        );
        if (resp) {
          toast.success(getValue(resp, `message`, ''));
          handleOpenPopup();
          setForm([]);
          setFormInfo({});
          getFormData();
          getData();
          if (getValue(resp, `data.id`, '')) {
            navigate(
              `/client/reports/detail/${getValue(
                resp,
                `data.id`,
                '',
              )}?${mainQueryRequest}`,
            );
          } else {
            navigate(`/client/reports?${mainQueryRequest}`);
          }
          simpleValidator.current.hideMessages();
          forceUpdate(0);
          setSubmitLoading(false);
        } else {
          setSubmitLoading(false);
        }
      } catch (error) {
        setSubmitLoading(false);
      }
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                 Filter section                             */
  /* -------------------------------------------------------------------------- */
  const handleNavigateURL = (key: string, value: string) => {
    if (value) {
      let payload = {
        ...urlParams,
        [key]: value,
      };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
    } else {
      delete urlParams[key];
      let payload = {
        ...urlParams,
      };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
      getData();
    }
  };
  const [filterOptions] = useState([
    { value: 'unsubmitted', label: formatText('unsubmitted') },
    { value: 'awaiting_approval', label: formatText('awaiting_approval') },
    { value: 'approved', label: formatText('approved') },
    { value: 'rejected', label: formatText('rejected') },
    { value: 'reimbursed', label: formatText('reimbursed') },
    // { value: "expired", label: formatText("expired") },
    // { value: "archived", label: formatText("archived") },
  ]);

  return (
    <DashboardLayout
      permissions={getValue(props, `tabPermissionList`, [])}
      subTabPermissionList={getValue(props, `subTabPermissionList`, [])}
    >
      {!isLoading &&
      !getValue(props, `subTabPermissionList`, []).includes('reports') ? (
        <NoAccessPage />
      ) : (
        <>
          <div className="dashboard-wrapper__header">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <h4 className="dashbaord-wrapper__header-title">
                Expense Reports
              </h4>

              {/* View As Dropdown - aligned to the right */}
              {createdForOptions.length > 1 && (
                <div className="d-flex align-items-center me-3">
                  <h6 className="small_text__14 color_primary mt-1 me-2">
                    View as:
                  </h6>
                  <div style={{ minWidth: '150px' }}>
                    <Select
                      styles={customStyles}
                      options={createdForOptions}
                      onChange={handleCreatedForChange}
                      value={createdFor}
                      isClearable={false}
                      placeholder="Select user"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          {getValue(selectedIDs, `length`, 0) > 0 && (
            <NavigationBar
              selectedIDs={selectedIDs}
              // isMarkInactiveSectionVisible={true}
              handleChangeActiveStatus={handleChangeActiveStatus}
              handleRemoveNavItems={handleRemoveNavItems}
              multi={
                getValue(props, `permissions`, []).includes('update')
                  ? true
                  : false
              }
              permissions={getValue(props, `permissions`, [])}
              entityMapping={
                getValue(props, `products`, []).includes('SELFCOMP')
                  ? true
                  : false
              }
              handleNavigateUserEntity={handleNavigateUserEntity}
            />
          )}
          <div className="admin-wrapper position-relative">
            <div className="admin-wrapper position-relative">
              <SearchHeader
                route={`/client/reports/add`}
                policy={userProfile?.policy}
                onShowGuidelines={handleGuidelinesModal}
                searchInput={search_text}
                showSearchTextbox={showSearchTextbox}
                toggleSearch={toggleSearch}
                toggleSearchClose={toggleSearchClose}
                handleChangeSearch={handleChangeSearch}
                add={
                  getValue(props, `permissions`, []).includes('create')
                    ? 'true'
                    : 'false'
                }
                isAdd={
                  getValue(props, `permissions`, []).includes('create')
                    ? true
                    : false
                }
                handleAddFun={handleOpenPopup}
                reportFilter
                filterOptions={filterOptions}
                handleNavigateURL={handleNavigateURL}
                filter_key={'approval_status'}
              />
            </div>
          </div>
          {isLoading || !props.permissionAPITriggered ? (
            // <Loader />
            <SkeletonTable />
          ) : getValue(list, `length`, 0) > 0 ? (
            <div
              className={
                getValue(selectedIDs, `length`, 0) > 0
                  ? 'datatable_main_container_notifiction'
                  : 'datatable_main_container'
              }
              style={{ overflowY: isLoading ? 'hidden' : 'auto' }}
            >
              <ReportsTable
                list={list}
                selectedIDs={selectedIDs}
                handleCheckUsers={handleCheckUsers}
                handleCheckAll={handleCheckAll}
                isLoading={isLoading}
                handleDeleteFun={handleDeleteFun}
                handleNavigate={handleNavigate}
              />
            </div>
          ) : (
            <>
              <ReportNotfound toggleReportNotFound={handleOpenPopup} />
            </>
          )}
          <div className="fixed_footer_wrapper d-flex justify-content-between">
            {list.length > 0 && (
              <img
                src="/images/building-illustration.svg"
                className="img-fluid"
              />
            )}
            {totalCount > 10 ? (
              <PaginationPage
                totalCount={totalCount}
                limit={limit}
                pageNumber={page_no}
                handleChangePagination={handleChangePagination}
                handleChangeLimit={handleChangeLimit}
              />
            ) : (
              <p className="show-rows-count mt-3 d-flex">
                Total Count:&nbsp;&nbsp;{' '}
                <p style={{ color: 'rgb(13, 110, 253)' }}>{totalCount}</p>
              </p>
            )}
          </div>
          <DeleteModal
            isOpen={isDelete}
            handleModal={handleDelete}
            handleSubmit={handleDeleteFunction}
            deleteValue={deleteValue}
          />
          <GuidelinesPopup
            isOpen={showGuidelines}
            handleModal={handleGuidelinesModal}
            guidelines={getValue(userProfile, 'policy.business_expense_guidelines', '')}
          />
          <ReportsPopup
            showUpdatePopup={showUpdatePopup}
            setShowUpdatePopup={setShowUpdatePopup}
            form={form}
            setForm={setForm}
            formInfo={formInfo}
            simpleValidator={simpleValidator}
            handleSubmit={handleSubmit}
            submitLoading={submitLoading}
            createdFor={createdFor}
            createdForOptions={createdForOptions}
            setCreatedFor={setCreatedFor}
          />
        </>
      )}
    </DashboardLayout>
  );
}

export default ClientReports;
